import { TemperatureChart } from './TemperatureChart';
import { PhChart } from './PhChart';
import { SalinityChart } from './SalinityChart';
import { AlkalinityChart } from './AlkalinityChart';
import { DualAxisNutrientChart } from './DualAxisNutrientChart';
import { extractWaterTestData, type DashboardData } from '@/hooks/useDashboardData';
import { CHARTS_PAGE_DEVICE_COLORS } from '@/utils/chartColors';
import type { ChartAnnotation } from '@/types/chartAnnotation';

/** Everything a dashboard chart needs from ChartsPage. */
export interface ChartRenderContext {
  dateRange: [Date, Date];
  dashboardData: DashboardData | undefined;
  isLoading: boolean;
  error: Error | undefined;
  annotations: ChartAnnotation[];
  expanded: boolean;
  onToggle: () => void;
  onAddNote: (parameter: string, parameterLabel: string, sources?: string[]) => void;
  onAnnotationClick: (annotation: ChartAnnotation) => void;
}

export type ChartId = 'temp' | 'ph' | 'salinity' | 'alk' | 'caMg' | 'no3Po4';

export interface ChartDefinition {
  id: ChartId;
  label: string;
  render: (ctx: ChartRenderContext) => JSX.Element;
}

/**
 * Human label for a chart id, INCLUDING the " chart" suffix (e.g. 'temp' →
 * 'Temperature chart'); falls back to `"{id} chart"`. One convention for all
 * user-facing chart naming: Add-menu entries, removal toasts, accessible
 * names and dnd announcements.
 */
export function chartLabelFor(id: string): string {
  return `${CHART_REGISTRY.find(c => c.id === id)?.label ?? id} chart`;
}

export const CHART_REGISTRY: ChartDefinition[] = [
  {
    id: 'temp',
    label: 'Temperature',
    render: ctx => (
      <TemperatureChart
        startDate={ctx.dateRange[0]}
        endDate={ctx.dateRange[1]}
        sensorData={ctx.dashboardData?.sensor_data}
        interval={ctx.dashboardData?.sensor_interval}
        isDownsampled={ctx.dashboardData?.sensor_downsampled}
        isAveraged={ctx.dashboardData?.sensor_downsampled}
        isLoading={ctx.isLoading}
        error={ctx.error}
        expanded={ctx.expanded}
        onToggle={ctx.onToggle}
        annotations={ctx.annotations}
        onAddNote={() => ctx.onAddNote('temperature', 'Temperature', ['apex'])}
        onAnnotationClick={ctx.onAnnotationClick}
      />
    ),
  },
  {
    id: 'ph',
    label: 'pH',
    render: ctx => (
      <PhChart
        startDate={ctx.dateRange[0]}
        endDate={ctx.dateRange[1]}
        sensorData={ctx.dashboardData?.sensor_data} // structurally satisfies PhChart's prop; no .map needed
        aquawizData={extractWaterTestData(ctx.dashboardData, 'ph', 'aquawiz')}
        alkatronicData={extractWaterTestData(ctx.dashboardData, 'ph', 'alkatronic')}
        manualData={extractWaterTestData(ctx.dashboardData, 'ph', 'manual')}
        kamoerData={extractWaterTestData(ctx.dashboardData, 'ph_kamoer', 'kamoer_kh')}
        targetRangesData={ctx.dashboardData?.target_ranges}
        isAveraged={ctx.dashboardData?.sensor_downsampled}
        isLoading={ctx.isLoading}
        error={ctx.error}
        expanded={ctx.expanded}
        onToggle={ctx.onToggle}
        annotations={ctx.annotations}
        onAddNote={() => ctx.onAddNote('ph', 'pH', ['apex', 'aquawiz', 'manual', 'kamoer_kh'])}
        onAnnotationClick={ctx.onAnnotationClick}
      />
    ),
  },
  {
    id: 'salinity',
    label: 'Salinity',
    render: ctx => (
      <SalinityChart
        startDate={ctx.dateRange[0]}
        endDate={ctx.dateRange[1]}
        sensorData={ctx.dashboardData?.sensor_data}
        manualData={extractWaterTestData(ctx.dashboardData, 'salinity', 'manual')}
        targetRangesData={ctx.dashboardData?.target_ranges}
        interval={ctx.dashboardData?.sensor_interval}
        isDownsampled={ctx.dashboardData?.sensor_downsampled}
        isAveraged={ctx.dashboardData?.sensor_downsampled}
        isLoading={ctx.isLoading}
        error={ctx.error}
        expanded={ctx.expanded}
        onToggle={ctx.onToggle}
        annotations={ctx.annotations}
        onAddNote={() => ctx.onAddNote('salinity', 'Salinity', ['apex', 'manual'])}
        onAnnotationClick={ctx.onAnnotationClick}
      />
    ),
  },
  {
    id: 'alk',
    label: 'Alkalinity',
    render: ctx => (
      <AlkalinityChart
        startDate={ctx.dateRange[0]}
        endDate={ctx.dateRange[1]}
        waterTestData={ctx.dashboardData?.water_test_data}
        targetRangesData={ctx.dashboardData?.target_ranges}
        isLoading={ctx.isLoading}
        error={ctx.error}
        expanded={ctx.expanded}
        onToggle={ctx.onToggle}
        annotations={ctx.annotations}
        onAddNote={() => ctx.onAddNote('alkalinity', 'Alkalinity', ['alkatronic', 'mastertronic', 'aquawiz', 'aquarimate', 'manual', 'kamoer_kh'])}
        onAnnotationClick={ctx.onAnnotationClick}
      />
    ),
  },
  {
    id: 'caMg',
    label: 'Calcium/Magnesium',
    render: ctx => (
      <DualAxisNutrientChart
        startDate={ctx.dateRange[0]}
        endDate={ctx.dateRange[1]}
        leftParam={{
          name: 'CA',
          label: 'Calcium',
          unit: 'ppm',
          ...CHARTS_PAGE_DEVICE_COLORS.aquarimate!,
          rangeKey: 'calcium',
        }}
        rightParam={{
          name: 'MG',
          label: 'Magnesium',
          unit: 'ppm',
          ...CHARTS_PAGE_DEVICE_COLORS.mastertronic!,
          rangeKey: 'magnesium',
        }}
        waterTestData={ctx.dashboardData?.water_test_data}
        targetRangesData={ctx.dashboardData?.target_ranges}
        isLoading={ctx.isLoading}
        error={ctx.error}
        expanded={ctx.expanded}
        onToggle={ctx.onToggle}
        annotations={ctx.annotations}
        onAddNoteLeft={() => ctx.onAddNote('calcium', 'Calcium', ['mastertronic', 'aquarimate', 'manual'])}
        onAddNoteRight={() => ctx.onAddNote('magnesium', 'Magnesium', ['mastertronic', 'aquarimate', 'manual'])}
        onAnnotationClick={ctx.onAnnotationClick}
      />
    ),
  },
  {
    id: 'no3Po4',
    label: 'Nitrate/Phosphate',
    render: ctx => (
      <DualAxisNutrientChart
        startDate={ctx.dateRange[0]}
        endDate={ctx.dateRange[1]}
        leftParam={{
          name: 'NO3',
          label: 'Nitrate',
          unit: 'ppm',
          ...CHARTS_PAGE_DEVICE_COLORS.manual!,
          rangeKey: 'nitrate',
        }}
        rightParam={{
          name: 'PO4',
          label: 'Phosphate',
          unit: 'ppm',
          ...CHARTS_PAGE_DEVICE_COLORS.aquawiz!,
          rangeKey: 'phosphate',
        }}
        waterTestData={ctx.dashboardData?.water_test_data}
        targetRangesData={ctx.dashboardData?.target_ranges}
        isLoading={ctx.isLoading}
        error={ctx.error}
        expanded={ctx.expanded}
        onToggle={ctx.onToggle}
        annotations={ctx.annotations}
        onAddNoteLeft={() => ctx.onAddNote('nitrate', 'Nitrate', ['mastertronic', 'aquarimate', 'manual'])}
        onAddNoteRight={() => ctx.onAddNote('phosphate', 'Phosphate', ['mastertronic', 'aquarimate', 'manual'])}
        onAnnotationClick={ctx.onAnnotationClick}
      />
    ),
  },
];
